import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, ArrowRight, Filter, Clock, Luggage, Wifi, Utensils, Check, Leaf, Sunrise, Sun, Sunset, Moon, Zap, PoundSterling } from "lucide-react";
import { api, money, durationLabel } from "@/lib/api";
import { AirlineLogo } from "@/components/AirlineLogo";
import { SearchWidget } from "@/components/SearchWidget";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";

const Stops = ({ n }) => (
  <span className={`text-xs font-bold ${n === 0 ? "text-emerald-600" : "text-slate-500"}`}>{n === 0 ? "Direct" : `${n} stop${n > 1 ? "s" : ""}`}</span>
);

const TIME_SLOTS = [
  { key: "morning", label: "Morning", sub: "05–11", icon: Sunrise, from: 5, to: 11 },
  { key: "afternoon", label: "Afternoon", sub: "12–17", icon: Sun, from: 12, to: 17 },
  { key: "evening", label: "Evening", sub: "18–23", icon: Sunset, from: 18, to: 23 },
  { key: "night", label: "Night", sub: "00–04", icon: Moon, from: 0, to: 4 },
];

const hourOf = (t) => parseInt(String(t).split(":")[0], 10) || 0;

export default function Flights() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const mode = params.get("mode") || "oneway";
  const cabin = params.get("cabin") || "economy";

  const legsDef = useMemo(() => {
    if (mode === "multi") {
      try { return JSON.parse(params.get("legs") || "[]"); } catch { return []; }
    }
    const origin = params.get("origin"), destination = params.get("destination"), date = params.get("date");
    if (!origin || !destination || !date) return [];
    const out = [{ origin, destination, date }];
    if (mode === "return") { const ret = params.get("ret"); if (ret) out.push({ origin: destination, destination: origin, date: ret }); }
    return out;
  }, [mode, params]);

  const [loading, setLoading] = useState(true);
  const [legResults, setLegResults] = useState([]);
  const [selections, setSelections] = useState([]);
  const [activeLeg, setActiveLeg] = useState(0);
  const [tab, setTab] = useState("best");
  const [directOnly, setDirectOnly] = useState(false);
  const [airlineFilter, setAirlineFilter] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [priceRange, setPriceRange] = useState(null);

  useEffect(() => {
    if (!legsDef.length) { setLoading(false); return; }
    setLoading(true);
    Promise.all(legsDef.map((l) => api.get("/flights/search", { params: { origin: l.origin, destination: l.destination, date: l.date, cabin } }).then((r) => r.data.results)))
      .then((all) => { setLegResults(all); setSelections(new Array(legsDef.length).fill(null)); setActiveLeg(0); })
      .finally(() => setLoading(false));
  }, [legsDef, cabin]);

  const current = useMemo(() => legResults[activeLeg] || [], [legResults, activeLeg]);
  const airlines = useMemo(() => [...new Map(current.map((f) => [f.airline_code, f.airline_name])).entries()], [current]);

  const priceBounds = useMemo(() => {
    if (!current.length) return [0, 2000];
    const ps = current.map((f) => f.price);
    return [Math.floor(Math.min(...ps)), Math.ceil(Math.max(...ps))];
  }, [current]);
  const durBounds = useMemo(() => {
    if (!current.length) return [0, 1];
    const ds = current.map((f) => f.duration_minutes);
    return [Math.min(...ds), Math.max(...ds)];
  }, [current]);

  useEffect(() => { setPriceRange(priceBounds); setTimeSlots([]); setDirectOnly(false); setAirlineFilter([]); }, [priceBounds, activeLeg]);

  const range = priceRange || priceBounds;

  const filtered = useMemo(() => {
    let out = [...current];
    if (directOnly) out = out.filter((f) => f.stops === 0);
    if (airlineFilter.length) out = out.filter((f) => airlineFilter.includes(f.airline_code));
    out = out.filter((f) => f.price >= range[0] && f.price <= range[1]);
    if (timeSlots.length) {
      out = out.filter((f) => timeSlots.some((k) => {
        const s = TIME_SLOTS.find((x) => x.key === k); const h = hourOf(f.depart_time);
        return h >= s.from && h <= s.to;
      }));
    }
    const [pMin, pMax] = priceBounds; const [dMin, dMax] = durBounds;
    const score = (f) => ((f.price - pMin) / ((pMax - pMin) || 1)) * 0.6 + ((f.duration_minutes - dMin) / ((dMax - dMin) || 1)) * 0.4;
    if (tab === "cheapest") out.sort((a, b) => a.price - b.price);
    else if (tab === "fastest") out.sort((a, b) => a.duration_minutes - b.duration_minutes);
    else out.sort((a, b) => score(a) - score(b));
    return out;
  }, [current, directOnly, airlineFilter, range, timeSlots, tab, priceBounds, durBounds]);

  const cheapest = useMemo(() => current.length ? Math.min(...current.map((f) => f.price)) : 0, [current]);
  const fastest = useMemo(() => current.length ? Math.min(...current.map((f) => f.duration_minutes)) : 0, [current]);
  const bestPrice = filtered[0]?.price || cheapest;

  const pick = (f) => {
    const next = [...selections]; next[activeLeg] = f; setSelections(next);
    const nextEmpty = next.findIndex((x) => !x);
    if (nextEmpty !== -1) { setActiveLeg(nextEmpty); }
  };

  const allSelected = selections.length > 0 && selections.every(Boolean);
  const total = selections.reduce((s, f) => s + (f?.price || 0), 0);

  const book = () => {
    const segments = selections.map((f) => ({
      airline: f.airline_name, airline_code: f.airline_code, flight_number: f.flight_number,
      origin: f.origin, destination: f.destination, origin_code: f.origin_code, destination_code: f.destination_code,
      depart: f.depart_time, arrive: f.arrive_time, duration: f.duration_label, stops: f.stops, date: f.depart_date,
    }));
    const routeTitle = segments.map((s) => `${s.origin_code}→${s.destination_code}`).join(" · ");
    const prefix = mode === "return" ? "Return: " : mode === "multi" ? "Multi-city: " : "";
    navigate("/checkout", { state: { item: {
      type: "flight", title: `${prefix}${routeTitle}`, amount: total, currency: "GBP", travel_date: segments[0].date,
      details: { airline: segments[0].airline, airline_code: segments[0].airline_code, cabin, trip_type: mode, segments,
        depart: segments[0].depart, arrive: segments[0].arrive, duration: selections[0].duration_label, stops: selections[0].stops },
    } } });
  };

  const TABS = [
    { key: "best", label: "Best", icon: Zap, meta: bestPrice ? money(bestPrice) : "" },
    { key: "cheapest", label: "Cheapest", icon: PoundSterling, meta: cheapest ? money(cheapest) : "" },
    { key: "fastest", label: "Fastest", icon: Clock, meta: fastest ? durationLabel(fastest) : "" },
  ];

  return (
    <div>
      <div className="bg-gradient-to-br from-brand to-[#7a0046] grain relative pt-8 pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto"><SearchWidget defaultTab="flights" /></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-14 relative">
        {!legsDef.length ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
            <Plane className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="font-heading font-bold text-xl text-slate-950">Search for your flight</h2>
            <p className="text-slate-500 mt-2">Choose one-way, return or multi-city above to compare fares.</p>
          </div>
        ) : (
          <>
            {legsDef.length > 1 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-2 mb-4 flex flex-wrap gap-2" data-testid="leg-steps">
                {legsDef.map((l, i) => (
                  <button key={i} onClick={() => setActiveLeg(i)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors ${activeLeg === i ? "bg-brand text-white" : selections[i] ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-600"}`}>
                    {selections[i] && <Check className="w-4 h-4" />} Flight {i + 1}: {l.origin} → {l.destination}
                    {selections[i] && <span className="opacity-80">· {money(selections[i].price)}</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Skyscanner-style Best / Cheapest / Fastest bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-2 mb-4 grid grid-cols-3 gap-2" data-testid="flight-tabs">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`flight-tab-${t.key}`}
                  className={`flex flex-col items-center sm:flex-row sm:justify-center gap-1 sm:gap-2 py-3 rounded-xl text-sm font-bold transition-all ${tab === t.key ? "bg-brand text-white shadow-md" : "text-slate-600 hover:bg-slate-50"}`}>
                  <span className="flex items-center gap-1.5"><t.icon className="w-4 h-4" />{t.label}</span>
                  <span className={`text-xs font-heading font-black ${tab === t.key ? "text-white/90" : "text-slate-400"}`}>{t.meta}</span>
                </button>
              ))}
            </div>

            <div className="grid lg:grid-cols-4 gap-6">
              <aside className="lg:col-span-1 space-y-4">
                <div className="bg-white rounded-2xl border border-slate-200 p-5 sticky top-24">
                  <div className="flex items-center gap-2 font-heading font-bold text-slate-950 mb-4"><Filter className="w-4 h-4" />Filter</div>

                  {/* Price range */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">Price</span>
                      <span className="text-xs font-bold text-slate-700">{money(range[0])} – {money(range[1])}</span>
                    </div>
                    <Slider data-testid="price-slider" value={range} min={priceBounds[0]} max={priceBounds[1]} step={5} onValueChange={setPriceRange} />
                  </div>

                  {/* Stops */}
                  <label className="flex items-center gap-3 cursor-pointer mb-4 pb-4 border-b border-slate-100" data-testid="filter-direct"><Checkbox checked={directOnly} onCheckedChange={setDirectOnly} /><span className="text-sm font-semibold text-slate-700">Direct flights only</span></label>

                  {/* Departure time */}
                  <div className="mb-4 pb-4 border-b border-slate-100">
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Departure time</div>
                    <div className="grid grid-cols-2 gap-2">
                      {TIME_SLOTS.map((s) => {
                        const active = timeSlots.includes(s.key);
                        return (
                          <button key={s.key} data-testid={`time-${s.key}`} onClick={() => setTimeSlots((p) => active ? p.filter((x) => x !== s.key) : [...p, s.key])}
                            className={`flex flex-col items-center gap-0.5 py-2.5 rounded-xl border text-xs font-semibold transition-colors ${active ? "bg-brand text-white border-brand" : "border-slate-200 text-slate-600 hover:border-brand"}`}>
                            <s.icon className="w-4 h-4" />{s.label}<span className={active ? "text-white/80" : "text-slate-400"}>{s.sub}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Airlines */}
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Airlines</div>
                    <div className="space-y-2.5 max-h-56 overflow-y-auto">
                      {airlines.map(([code, name]) => (
                        <label key={code} className="flex items-center gap-3 cursor-pointer">
                          <Checkbox checked={airlineFilter.includes(code)} onCheckedChange={(c) => setAirlineFilter((p) => c ? [...p, code] : p.filter((x) => x !== code))} />
                          <AirlineLogo code={code} className="w-6 h-6" /><span className="text-sm text-slate-700 truncate">{name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>

              <div className="lg:col-span-3">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div>
                    <h1 className="font-heading font-black text-2xl text-slate-950">{legsDef[activeLeg]?.origin} → {legsDef[activeLeg]?.destination}</h1>
                    <p className="text-sm text-slate-500">{loading ? "Searching best fares…" : `${filtered.length} of ${current.length} flights · ${legsDef[activeLeg]?.date} · ${cabin}`}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {loading ? Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />) :
                    filtered.length === 0 ? (
                      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <Plane className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500">No flights match your filters. Try widening the price range.</p>
                      </div>
                    ) : filtered.map((f, i) => {
                      const selected = selections[activeLeg]?.id === f.id;
                      const isCheapest = f.price === cheapest;
                      const isFastest = f.duration_minutes === fastest;
                      return (
                        <motion.div key={f.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 8) * 0.03 }} data-testid={`flight-card-${i}`}
                          className={`bg-white rounded-2xl border p-5 transition-all ${selected ? "border-brand ring-2 ring-brand/20" : "border-slate-200 hover:shadow-lg hover:border-brand/30"}`}>
                          {(isCheapest || isFastest) && (
                            <div className="flex gap-2 mb-3">
                              {isCheapest && <span className="text-[10px] font-black uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Cheapest</span>}
                              {isFastest && <span className="text-[10px] font-black uppercase tracking-wide bg-brand/10 text-brand px-2 py-0.5 rounded-full">Fastest</span>}
                            </div>
                          )}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
                            <div className="flex items-center gap-3 sm:w-44">
                              <AirlineLogo code={f.airline_code} name={f.airline_name} className="w-11 h-11" />
                              <div><div className="font-bold text-sm text-slate-950 leading-tight">{f.airline_name}</div><div className="text-xs text-slate-400">{f.flight_number}</div></div>
                            </div>
                            <div className="flex items-center gap-4 flex-1">
                              <div className="text-center"><div className="font-heading font-black text-xl text-slate-950">{f.depart_time}</div><div className="text-xs font-bold text-slate-400">{f.origin_code}</div></div>
                              <div className="flex-1 flex flex-col items-center px-2">
                                <div className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{f.duration_label}</div>
                                <div className="w-full h-px bg-slate-200 my-1.5 relative"><Plane className="w-3.5 h-3.5 text-brand absolute right-0 -top-1.5" /></div>
                                <Stops n={f.stops} />
                                {f.layovers?.length > 0 && <div className="text-[10px] text-slate-400 mt-0.5">via {f.layovers.map((l) => l.code).join(", ")}</div>}
                              </div>
                              <div className="text-center"><div className="font-heading font-black text-xl text-slate-950">{f.arrive_time}</div><div className="text-xs font-bold text-slate-400">{f.destination_code}</div></div>
                            </div>
                            <div className="sm:w-40 flex sm:flex-col items-center sm:items-end justify-between gap-2 border-t sm:border-t-0 sm:border-l border-slate-100 pt-3 sm:pt-0 sm:pl-5">
                              <div className="text-right"><div className="text-xs text-slate-400">from</div><div className="font-heading font-black text-2xl text-slate-950">{money(f.price)}</div></div>
                              <Button onClick={() => pick(f)} data-testid={`select-flight-${i}`} className={`rounded-full gap-1 ${selected ? "bg-emerald-600 hover:bg-emerald-700" : "bg-brand hover:bg-brand-hover"}`}>{selected ? <><Check className="w-4 h-4" />Selected</> : <>Select<ArrowRight className="w-4 h-4" /></>}</Button>
                            </div>
                          </div>
                          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                            <span className="flex items-center gap-1"><Luggage className="w-3.5 h-3.5" />{f.baggage}</span>
                            <span>{f.aircraft}</span>
                            {f.wifi && <span className="flex items-center gap-1"><Wifi className="w-3.5 h-3.5" />Wi-Fi</span>}
                            {f.meal && <span className="flex items-center gap-1"><Utensils className="w-3.5 h-3.5" />Meal</span>}
                            <span className="flex items-center gap-1"><Leaf className="w-3.5 h-3.5 text-emerald-500" />{f.emissions_kg}kg CO₂</span>
                            <span className="text-brand font-bold ml-auto">Only {f.seats_left} seats left</span>
                          </div>
                        </motion.div>
                      );
                    })}
                </div>
              </div>
            </div>

            {(legsDef.length === 1 ? selections[0] : allSelected) && (
              <motion.div initial={{ y: 60 }} animate={{ y: 0 }} className="sticky bottom-4 mt-6 z-30">
                <div className="bg-slate-950 text-white rounded-2xl p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 shadow-2xl">
                  <div className="flex items-center gap-4">
                    <div className="text-xs uppercase tracking-wide text-slate-400">{mode === "return" ? "Return trip" : mode === "multi" ? `${selections.length} flights` : "Your flight"}</div>
                    <div className="font-heading font-black text-2xl">{money(total)}</div>
                  </div>
                  <Button onClick={book} data-testid="continue-booking" className="rounded-full bg-brand hover:bg-brand-hover h-12 px-8 font-heading font-bold gap-2">Continue to book <ArrowRight className="w-5 h-5" /></Button>
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
